import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { useAdminTheme } from '../../admin-theme';
import { darkTableSx } from './ui';
import { VAPERITE_SELL_IN_APP_TAG as TAG } from './sellInAppTag';

// Vaperite tags what it sells through Deeliver with the Sell in app tag in Vend. Every manual route
// that lets a new item through (approve, bulk approve, approve all, un-exclude) is
// held by the server when that tag is missing, and this dialog asks the person:
// let it through anyway, exclude it, or skip it for now to investigate.

export interface NoWooItem { sku: string; name: string }

/** The reason recorded when a person excludes items from this dialog. */
export const NO_WOOCOMMERCE_REASON = `No ${TAG} tag in Vend`;

const SHOWN = 50;

interface Props {
  open: boolean;
  items: NoWooItem[];
  /** Items behind the list when it is a count only (Approve All); defaults to items.length. */
  total?: number;
  /** 'approve' (new items) or 'unexclude' (back from Excluded Products). */
  mode?: 'approve' | 'unexclude';
  busy?: boolean;
  onConfirm: () => void;
  /** Omitted when excluding makes no sense (un-exclude: it is excluded already). */
  onExclude?: () => void;
  onSkip: () => void;
}

const plural = (n: number) => `${n} item${n === 1 ? '' : 's'}`;

export default function NoWoocommerceDialog({
  open, items, total, mode = 'approve', busy = false, onConfirm, onExclude, onSkip,
}: Props) {
  const { colors } = useAdminTheme();
  const count = total ?? items.length;
  const verb = mode === 'unexclude' ? 'Un-exclude' : 'Approve';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ color: colors.textPrimary }}>No {TAG} tag in Vend</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          {count === 1 ? 'This item is' : `These ${plural(count)} are`} not tagged {TAG} in Vaperite&apos;s Vend,
          so Vaperite may not sell {count === 1 ? 'it' : 'them'} through Deeliver. {verb} anyway, exclude, or skip for now to
          investigate ({count === 1 ? 'it stays' : 'they stay'} where {count === 1 ? 'it is' : 'they are'}).
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: colors.textMuted, mb: 2 }}>
          Let through anyway, an item keeps its Sell in app setting — off for untagged items unless someone
          switched it on — so it can be created but hidden from the app.
        </Typography>
        {items.length > 0 && (
          <TableContainer sx={{ ...darkTableSx, maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.slice(0, SHOWN).map((i) => (
                  <TableRow key={i.sku}>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{i.sku}</TableCell>
                    <TableCell>{i.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {count > Math.min(items.length, SHOWN) && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {items.length > 0 ? `…and ${count - Math.min(items.length, SHOWN)} more. ` : ''}
              Pending Review&apos;s &quot;No {TAG} tag&quot; filter lists them (it filters on Sell in app, so an
              item switched on by hand will not show there).
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSkip} disabled={busy} sx={{ color: colors.textSecondary }}>Skip for now</Button>
        {onExclude && (
          <Button onClick={onExclude} disabled={busy} color="error" variant="outlined">Exclude</Button>
        )}
        <Button onClick={onConfirm} disabled={busy} color="warning" variant="contained">{verb} anyway</Button>
      </DialogActions>
    </Dialog>
  );
}
